import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Flex, Text, Button, Separator, IconButton, Select, Grid, Badge, Tooltip, Box } from '@radix-ui/themes';
import { ArrowLeftIcon, ArrowRightIcon, PlusIcon } from '@radix-ui/react-icons';

// Custom component to handle text truncation and tooltip for plan titles
const TruncatedText = ({ children, size, weight, color, tooltipContent, showTitleInBadge=false }) => {
  const text = typeof children === 'string' ? children : String(children);
  const [isTruncated, setIsTruncated] = useState(false);
  const containerRef = useRef(null);
  
  useEffect(() => {
    const checkTruncation = () => {
      const container = containerRef.current;
      if (container) {
        setIsTruncated(container.scrollWidth > container.clientWidth);
      }
    };
    
    // Initial check
    checkTruncation();
    
    // Recheck on window resize
    window.addEventListener('resize', checkTruncation);
    
    return () => window.removeEventListener('resize', checkTruncation);
  }, [text]);

  // Extract title and remaining text if showTitleInBadge is true
  const parsedContent = showTitleInBadge ? (() => {
    // Look for pattern: "Title (date - date)" or just "Title"
    const match = text.match(/^([^(]+)(\s*\(.+\))?$/);
    if (match) {
      const title = match[1].trim();
      const dateInfo = match[2] || '';
      return { title, dateInfo };
    }
    return { title: text, dateInfo: '' };
  })() : null;
  
  const textElement = showTitleInBadge && parsedContent ? (
    <Box as="div" 
      ref={containerRef} 
      style={{
        width: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        alignItems: 'center',
      }}
    >
      <Badge size="1" variant="soft" color="gray"
        style={{ marginRight: '4px', display: 'inline-flex', alignItems: 'center' }}>
        {parsedContent.title}
      </Badge>
      {parsedContent.dateInfo && (
        <Text 
          size={size} 
          weight={weight}
          color={color}
          style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}
        >
          {parsedContent.dateInfo}
        </Text>
      )}
    </Box>
  ) : (
    <Box as="div" 
      ref={containerRef} 
      style={{
        width: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
      }}
    >
      <Text 
        size={size} 
        weight={weight}
        color={color}
      >
        {text}
      </Text>
    </Box>
  );
  
  // Use custom tooltip content if provided, otherwise show truncated text only when truncated
  const shouldShowTooltip = tooltipContent || isTruncated;
  const content = tooltipContent || text;
  
  return shouldShowTooltip ? (
    <Tooltip content={content}>
      {textElement}
    </Tooltip>
  ) : textElement;
};

const Calendar = ({ onDateSelect, plans = [], addEventButton, onAddEventForDate,
  CreatePlanPopover, planTitle, setPlanTitle, planStartDate, setPlanStartDate, 
  planEndDate, setPlanEndDate, onCreatePlan, isCreating, selectedPlan, onPlanSelect, 
  validationError, setValidationError, onPlanUpdate }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [hoveredDate, setHoveredDate] = useState(null);
  const [createPopoverOpen, setCreatePopoverOpen] = useState(false);
  const [createPopoverDay, setCreatePopoverDay] = useState(null);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    
    let firstDayOfWeek = firstDay.getDay();
    firstDayOfWeek = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add actual days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }
    
    // Add empty cells to complete the grid (total cells should be multiple of 7)
    const totalCells = Math.ceil(days.length / 7) * 7;
    while (days.length < totalCells) {
      days.push(null);
    }
    
    return days;
  };

  // Get plans for a specific date
  const getPlansForDate = (day) => {
    if (!day) return [];
    
    const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    return plans.filter(plan => {
      const [startYear, startMonth, startDay] = plan.start_date.split('-');
      const [endYear, endMonth, endDay] = plan.end_date.split('-');
      const planStart = new Date(parseInt(startYear), parseInt(startMonth) - 1, parseInt(startDay));
      const planEnd = new Date(parseInt(endYear), parseInt(endMonth) - 1, parseInt(endDay));
      planEnd.setDate(planEnd.getDate() - 1); // End date is exclusive, so subtract 1 day
      return date >= planStart && date <= planEnd;
    });
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const handleMonthChange = (monthIndex) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(parseInt(monthIndex));
    setCurrentDate(newDate);
  };

  const handleYearChange = (year) => {
    const newDate = new Date(currentDate);
    newDate.setFullYear(parseInt(year));
    setCurrentDate(newDate);
  };

  // Generate year options (current year ± 2 years)
  const getYearOptions = () => {
    const currentYear = currentDate.getFullYear();
    const years = [];
    for (let i = currentYear - 2; i <= currentYear + 2; i++) {
      years.push(i);
    }
    return years;
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentDate(today);
    setSelectedDate(today.getDate());
    onDateSelect?.(today);
  };

  const isToday = (day) => {
    const today = new Date();
    return (
      day &&
      today.getDate() === day &&
      today.getMonth() === currentDate.getMonth() &&
      today.getFullYear() === currentDate.getFullYear()
    );
  };

  const isTodayOrFuture = (day) => {
    if (!day) return false;
    const today = new Date();
    const dayDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    
    // Reset time parts to compare only dates
    today.setHours(0, 0, 0, 0);
    dayDate.setHours(0, 0, 0, 0);
    
    return dayDate >= today;
  };

  const days = getDaysInMonth(currentDate);
  const totalRows = Math.ceil(days.length / 7);
  const calendarGridRef = useRef(null);

  // Calculate plan overlays for absolute positioning
  const getPlanOverlays = () => {
    const overlays = [];
    const plansByRow = {}; // Track plans by row to handle vertical stacking

    plans.forEach(plan => {
      const [startYear, startMonth, startDayStr] = plan.start_date.split('-');
      const [endYear, endMonth, endDayStr] = plan.end_date.split('-');
      const planStart = new Date(parseInt(startYear), parseInt(startMonth) - 1, parseInt(startDayStr));
      const planEnd = new Date(parseInt(endYear), parseInt(endMonth) - 1, parseInt(endDayStr));
      planEnd.setDate(planEnd.getDate() - 1); // End date is exclusive

      // Check if plan overlaps with current month
      const monthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

      if (planEnd < monthStart || planStart > monthEnd) return;

      // Calculate visible start and end within current month
      const visibleStart = planStart < monthStart ? monthStart : planStart;
      const visibleEnd = planEnd > monthEnd ? monthEnd : planEnd;

      // Calculate grid positions
      const visibleStartDay = visibleStart.getDate();
      const visibleEndDay = visibleEnd.getDate();

      // Find positions in the days array
      const startIndex = days.findIndex(day => day === visibleStartDay);
      const endIndex = days.findIndex(day => day === visibleEndDay);

      if (startIndex === -1 || endIndex === -1) return;

      const startRow = Math.floor(startIndex / 7);
      const startCol = startIndex % 7;
      const endRow = Math.floor(endIndex / 7);
      const endCol = endIndex % 7;

      // Handle multi-row plans by creating separate overlays for each row
      for (let row = startRow; row <= endRow; row++) {
        const rowStartCol = row === startRow ? startCol : 0;
        const rowEndCol = row === endRow ? endCol : 6;
        
        // Initialize row tracking if needed
        if (!plansByRow[row]) plansByRow[row] = [];
        
        // Find available vertical position (level) for this plan in this row
        let level = 0;
        const existingPlans = plansByRow[row];
        
        // Check for conflicts with existing plans at each level
        while (level < existingPlans.length) {
          const hasConflict = existingPlans[level].some(existingPlan => {
            return !(rowEndCol < existingPlan.startCol || rowStartCol > existingPlan.endCol);
          });
          
          if (!hasConflict) break;
          level++;
        }
        
        // Initialize level array if needed
        if (!existingPlans[level]) existingPlans[level] = [];
        
        // Add this plan segment to the level
        const planSegment = {
          startCol: rowStartCol,
          endCol: rowEndCol,
          plan: plan
        };
        existingPlans[level].push(planSegment);

        overlays.push({
          ...plan,
          id: `${plan.id}-row-${row}`,
          originalId: plan.id,
          row: row,
          startCol: rowStartCol,
          endCol: rowEndCol,
          level: level,
          isFirstRow: row === startRow,
          isLastRow: row === endRow,
          isSingleRow: startRow === endRow,
          // Check if this is a continuation from previous month
          isContinuationFromPrevMonth: planStart < monthStart,
          // Check if this continues to next month  
          isContinuationToNextMonth: planEnd > monthEnd,
          width: ((rowEndCol - rowStartCol + 1) / 7) * 100, // Percentage width
          left: (rowStartCol / 7) * 100, // Percentage left position
          top: 40 + (level * 35), // Base offset + level stacking
          paddingAdjustment: {
            left: row === startRow ? 8 : 0, // Add padding to first segment
            right: row === endRow ? 8 : 0   // Add padding to last segment
          },
        });
      }
    });

    return overlays;
  };

  const planOverlays = getPlanOverlays();

  const handlePlanSelect = (plan) => {
    if (onPlanSelect) {
      // If the same plan is clicked, deselect it
      if (selectedPlan && selectedPlan.id === plan.originalId) {
        onPlanSelect(null);
      } else {
        // Create a plan object with the original ID for consistency
        const planToSelect = {
          ...plan,
          id: plan.originalId
        };
        onPlanSelect(planToSelect);
      }
    }
  };

  // Helper function to determine if a plan is past, current, or future
  const getPlanTimeStatus = (plan) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const [startYear, startMonth, startDay] = plan.start_date.split('-');
    const [endYear, endMonth, endDay] = plan.end_date.split('-');
    const planStart = new Date(parseInt(startYear), parseInt(startMonth) - 1, parseInt(startDay));
    const planEnd = new Date(parseInt(endYear), parseInt(endMonth) - 1, parseInt(endDay));
    planEnd.setDate(planEnd.getDate() - 1); // End date is exclusive
    
    if (planEnd < today) {
      return 'past';
    } else if (planStart <= today && planEnd >= today) {
      return 'current';
    } else {
      return 'future';
    }
  };

  // Helper function to get plan colors based on time status
  const getPlanColors = (plan, isSelected, isHovered) => {
    const timeStatus = getPlanTimeStatus(plan);
    
    if (timeStatus === 'current') {
      // Blue colors for active/current plans only
      return {
        base: 'var(--blue-4)',
        border: isSelected ? '1px solid var(--blue-a6)' : 'none',
      };
    } else {
      // Gray colors for both past and future plans
      return {
        base: 'var(--gray-4)',
        border: isSelected ? '1px solid var(--gray-a4)' : 'none',
      };
    }
  };

  const handleDateClick = (day) => {
    if (day) {
      setSelectedDate(day);
      const selectedFullDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      
      // Simply select the date without automatically editing plans
      // Plans can now be selected by clicking on them directly
      if (isTodayOrFuture(day)) {
        onDateSelect?.(selectedFullDate);
      }
    }
  };

  const formatDate = (dateString, adjustment = 0) => {
    const [year, month, day] = dateString.split('-');
    const d = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    d.setDate(d.getDate() + adjustment);
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  };

  // Function to deselect plan when clicking on empty areas
  const handleBackgroundClick = () => {
    if (selectedPlan && onPlanSelect) {
      onPlanSelect(null);
    }
  };

  return (
    <Flex 
      direction="column" 
      onClick={handleBackgroundClick}
      style={{ alignItems: 'flex-start', alignSelf: 'stretch', flex: '1 0 0' }}>
        {/* Header */}
        <Flex 
            gap="2" 
            p="4" 
            onClick={(e) => e.stopPropagation()} // Prevent deselection when clicking header elements
            style={{ justifyContent: 'space-between', alignItems: "center", alignSelf: 'stretch',
            background: "var(--color-panel-solid)", width: '100%' }}>
            <Flex gap="2" style={{ alignItems: 'center' }}>
                <IconButton size="2" variant="soft" color="gray" onClick={() => navigateMonth(-1)}>
                    <ArrowLeftIcon width={16} height={16} />
                </IconButton>
                {/* Month select */}
                <Select.Root
                  value={currentDate.getMonth().toString()} 
                  onValueChange={handleMonthChange}>
                  <Select.Trigger
                    variant="soft"
                    color="gray"
                    style={{ justifyContent: 'center' }} />
                  <Select.Content variant='soft' style={{ maxHeight: '200px'}}>
                    <Select.Group>
                      {monthNames.map((month, index) => (
                        <Select.Item key={index} value={index.toString()}>
                          {month}
                        </Select.Item>
                      ))}
                    </Select.Group>
                  </Select.Content>
                </Select.Root>
                {/* Year select */}
                <Select.Root 
                  value={currentDate.getFullYear().toString()}
                  onValueChange={handleYearChange}>
                  <Select.Trigger
                    variant="soft"
                    color="gray"/>
                  <Select.Content>
                    <Select.Group>
                      {getYearOptions().map((year) => (
                        <Select.Item key={year} value={year.toString()}>
                          {year}
                        </Select.Item>
                      ))}
                    </Select.Group>
                  </Select.Content>
                </Select.Root>
                <IconButton size="2" variant="soft" color="gray" onClick={() => navigateMonth(1)}>
                    <ArrowRightIcon width={16} height={16} />
                </IconButton>
                <Button variant="outline" size="2" color="gray" onClick={goToToday}>
                    Today
                </Button>
            </Flex>
            <Flex gap="2" style={{ alignItems: 'center'}}>
              {addEventButton}
            </Flex>
        </Flex>
        <Separator size="4" orientation="horizontal" style={{ minHeight: "1px" }} />
        {/* Calendar Grid */}
        <Flex direction="column" style={{ alignSelf: 'stretch', width: '100%',
            background: "var(--color-panel-solid)", height: '100%' }}>
            {/* Name of the day headers */}
            <Grid 
                columns="7" 
                onClick={(e) => e.stopPropagation()} // Prevent deselection when clicking day headers
                style={{ borderBottom: '1px dashed var(--gray-6)'}}>
                {dayNames.map((day, index) => (
                    <Flex key={index} justify="center" align="center" style={{ minHeight: '80px',
                        borderRight: index < 6 ? '1px dashed var(--gray-6)' : 'none'}}>
                        <Text key={day} size="2" color="gray" align="center" weight="medium">{day}</Text>
                    </Flex>
                ))}
            </Grid>

            {/* Calendar days */}
            <div ref={calendarGridRef} style={{ position: 'relative', height: '100%' }}>
                <Grid columns="7" style={{ height: '100%' }}>
                    {days.map((day, index) => {
                        const dayPlans = getPlansForDate(day);
                        const hasPlans = dayPlans.length > 0;
                        const rowIndex = Math.floor(index / 7);
                        const colIndex = index % 7;
                        const isHovered = hoveredDate === day;
                        
                        return (
                            <Flex
                                key={index}
                                onClick={(e) => {
                                    e.stopPropagation(); // Prevent deselection when clicking on dates
                                    handleDateClick(day);
                                }}
                                onMouseEnter={() => setHoveredDate(day)}
                                onMouseLeave={() => {
                                  // Don't clear hovered date if popover is open for this day
                                  if (!(createPopoverOpen && createPopoverDay === day)) {
                                    setHoveredDate(null);
                                  }
                                }}
                                style={{
                                    height: '100%',
                                    backgroundColor: 'var(--color-panel-solid)',
                                    borderRight: colIndex < 6 ? '1px dashed var(--gray-6)' : 'none',
                                    borderBottom: rowIndex < (totalRows - 1) ? '1px dashed var(--gray-6)' : 'none',
                                    padding: '8px',
                                    flexDirection: 'column',
                                    justifyContent: 'flex-start',
                                    position: 'relative',
                                    userSelect: 'none',
                                }}>
                                {day && (
                                    <>
                                        {/* Day number */}
                                        {isToday(day) ? (
                                          <Flex justify="end">
                                              <Badge color="blue" variant="solid" size="2">
                                                  {day}
                                              </Badge>
                                          </Flex>
                                        ) : (
                                          <Text size="2" weight="medium" color="gray" align="right"
                                            style={{ opacity: isTodayOrFuture(day) ? 1 : 0.5 }}>
                                            {day}
                                          </Text>
                                        )}
                                        
                                        {/* Show plus icon only for days without plans on hover (today/future) */}
                                        {((!hasPlans && isHovered && isTodayOrFuture(day)) || (createPopoverOpen && createPopoverDay === day)) && (
                                            <div style={{ position: 'absolute', top: '0', left: '0', right: '0', bottom: '0', zIndex: 20 }}>
                                                <CreatePlanPopover
                                                    isOpen={createPopoverOpen && createPopoverDay === day}
                                                    onOpenChange={(open) => {
                                                        setCreatePopoverOpen(open);
                                                        if (!open) {
                                                            setCreatePopoverDay(null);
                                                            setHoveredDate(null);
                                                        }
                                                    }}
                                                    planTitle={planTitle}
                                                    setPlanTitle={setPlanTitle}
                                                    planStartDate={planStartDate}
                                                    setPlanStartDate={setPlanStartDate}
                                                    planEndDate={planEndDate}
                                                    setPlanEndDate={setPlanEndDate}
                                                    onCreatePlan={onCreatePlan}
                                                    isCreating={isCreating}
                                                    isEditing={false}
                                                    validationError={validationError}
                                                    setValidationError={setValidationError}
                                                >
                                                    <Flex
                                                        style={{
                                                            position: 'absolute',
                                                            top: '50%',
                                                            left: '50%',
                                                            transform: 'translate(-50%, -50%)',
                                                            zIndex: 10
                                                        }}
                                                    >
                                                        <IconButton
                                                            size="2"
                                                            variant="ghost"
                                                            color="gray"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                e.preventDefault();
                                                                
                                                                // Set up the single day plan
                                                                const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
                                                                
                                                                // Clear any existing state
                                                                if (setValidationError) setValidationError("");
                                                                if (setPlanTitle) setPlanTitle("");
                                                                
                                                                // Set dates for single day selection - same date for start and end
                                                                const formattedDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                                                                
                                                                setPlanStartDate(formattedDate);
                                                                setPlanEndDate(formattedDate);
                                                                
                                                                // Open the popover
                                                                setCreatePopoverOpen(true);
                                                                setCreatePopoverDay(day);
                                                            }}
                                                            onMouseDown={(e) => {
                                                                e.stopPropagation();
                                                            }}
                                                            onMouseUp={(e) => {
                                                                e.stopPropagation();
                                                            }}
                                                        >
                                                            <PlusIcon width={12} height={12} />
                                                        </IconButton>
                                                    </Flex>
                                                </CreatePlanPopover>
                                            </div>
                                        )}
                                    </>
                                )}
                            </Flex>
                        );
                    })}
                </Grid>
                
                {/* Plan overlays */}
                {planOverlays.map((overlay) => {
                  const isSelected = selectedPlan && selectedPlan.id === overlay.originalId;
                  const planColors = getPlanColors(overlay, isSelected, false);
                  
                  // Create the full text to display
                  const fullPlanText = `${overlay.title} (${formatDate(overlay.start_date)} - ${formatDate(overlay.end_date, -1)})`;
                  
                  return (
                      <div
                          key={overlay.id}
                          style={{
                              position: 'absolute',
                              top: `calc(${overlay.row * (100 / totalRows)}% + ${overlay.top}px)`,
                              left: `calc(${overlay.left}% + ${overlay.paddingAdjustment.left}px)`,
                              width: `calc(${overlay.width}% - ${overlay.paddingAdjustment.left + overlay.paddingAdjustment.right}px)`,
                              height: '35px',
                              backgroundColor: planColors.base,
                              border: planColors.border,
                              borderRadius: overlay.isSingleRow 
                                ? '4px' 
                                : overlay.isFirstRow 
                                  ? (overlay.isContinuationFromPrevMonth ? '0 4px 4px 0' : '4px 0 0 4px')
                                  : overlay.isLastRow 
                                    ? (overlay.isContinuationToNextMonth ? '0 4px 4px 0' : '0 4px 4px 0')
                                    : '0',
                              display: 'flex',
                              alignItems: 'center',
                              paddingLeft: '8px',
                              paddingRight: '8px',
                              overflow: 'hidden',
                              zIndex: isSelected ? 15 : 10,
                          }}
                          onClick={(e) => {
                              e.stopPropagation(); // Prevent event bubbling to parent
                              handlePlanSelect(overlay);
                          }}
                          tabIndex={0}
                          onKeyDown={(e) => {
                              // Allow keyboard navigation for accessibility
                              if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  handlePlanSelect(overlay);
                              }
                          }}
                          aria-label={`Plan: ${overlay.title} from ${formatDate(overlay.start_date)} to ${formatDate(overlay.end_date, -1)}.`}
                          role="button"
                      >
                          {/* Show text based on plan span and month boundaries */}
                          {(() => {
                            // Case 1: First row of a plan that starts in this month (not continuation)
                            if (overlay.isFirstRow && !overlay.isContinuationFromPrevMonth) {
                              return (
                                <TruncatedText 
                                    size="1" 
                                    weight="medium" 
                                    color="gray"
                                    showTitleInBadge={true}
                                >
                                    {fullPlanText}
                                </TruncatedText>
                              );
                            }
                            
                            // Case 2: Last row of a plan that continues to next month - show truncated with tooltip
                            if (overlay.isLastRow && overlay.isContinuationToNextMonth) {
                              return (
                                <TruncatedText 
                                    size="1" 
                                    weight="medium" 
                                    color="gray"
                                    showTitleInBadge={true}
                                    tooltipContent={fullPlanText}
                                >
                                    {fullPlanText}
                                </TruncatedText>
                              );
                            }
                            
                            // Case 3: First row of a plan that continues from previous month - show full text
                            if (overlay.isFirstRow && overlay.isContinuationFromPrevMonth) {
                              return (
                                <TruncatedText 
                                    size="1" 
                                    weight="medium" 
                                    color="gray"
                                    showTitleInBadge={true}
                                >
                                    {fullPlanText}
                                </TruncatedText>
                              );
                            }
                            
                            // Case 4: All other segments (middle rows of multi-row plans) - show no text
                            return null;
                          })()}
                      </div>
                  );
                })}
            </div>
        </Flex>
    </Flex>
  );
};

export default Calendar;